import React, {
    FC,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import {cn} from '@bem-react/classname';
import {Button, useFileInput} from '@gravity-ui/uikit';
import {FileDropZone} from '@yandex-data-ui/common';
import {FileValue} from 'common/models/survey';
import {FilePath} from 'common/schema/forms/v3';
import getTextInputAria from 'ui/utils/getTextInputAria';

import FileBadge from '../FileBadge';
import QuestionLayout, {QuestionLayoutType} from '../QuestionLayout';

import {buttonProps, fileQuestionPositions} from './FileQuestion.constants';
import {FileQuestionProps} from './FileQuestion.types';

import './FileQuestion.scss';

const cnFileQuestion = cn('FileQuestion');

const FileQuestion: FC<FileQuestionProps> = forwardRef<
    Partial<HTMLButtonElement>,
    FileQuestionProps
>(function FileQuestion(
    {
        className,
        upload,
        deleteFile,
        onLoad,
        maxCount,
        hint,
        label,
        buttonIcon,
        buttonText,
        drugText,
        value,
        ...props
    },
    ref,
) {
    const [files, setFiles] = useState<FileValue[]>(value ?? []);
    const inputRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            focus: () => {
                inputRef.current?.focus();
            },
        }),
        [],
    );

    const onSelectFiles = useCallback(
        (selectedFiles: File[]) => {
            inputRef.current?.focus();
            setFiles((currentFiles) => {
                currentFiles.push(
                    ...selectedFiles.map<FileValue>((file) => ({
                        name: file.name,
                        loading: true,
                    })),
                );
                return [...currentFiles];
            });
            selectedFiles.forEach((file) => {
                upload(file)
                    .then((fileData) => {
                        if (fileData) {
                            setFiles((currentFiles) => {
                                const index = currentFiles.findIndex(
                                    (currentFile) =>
                                        currentFile.loading && currentFile.name === fileData.name,
                                );
                                currentFiles[index] = fileData;
                                return [...currentFiles];
                            });
                        }
                    })
                    .catch(() => {
                        setFiles((currentFiles) => {
                            return currentFiles.filter(
                                (currentFile) =>
                                    !currentFile.loading || currentFile.name !== file.name,
                            );
                        });
                    });
            });
        },
        [upload],
    );

    const onDelete = useCallback(
        (removeItem: FileValue, removeIndex: number) => () => {
            const tmpFiles = [...files];
            setFiles((currentFiles) => {
                return [
                    ...currentFiles.filter(
                        (currentFile, index) =>
                            !(index === removeIndex && currentFile.name === removeItem.name) &&
                            (!removeItem.path || currentFile.path !== removeItem.path),
                    ),
                ];
            });
            if (removeItem.path) {
                deleteFile(removeItem as FilePath).catch(() => {
                    setFiles(tmpFiles);
                });
            }
        },
        [],
    );

    useEffect(() => {
        if (!onLoad) return;
        onLoad(
            files.reduce<FileValue[]>((acc, file) => {
                if (!file.loading && file.path && file.url) {
                    acc.push(file);
                }
                return acc;
            }, []),
        );
    }, [files, onLoad]);

    const {controlProps, triggerProps} = useFileInput({
        onUpdate: onSelectFiles,
    });

    const [drag, setDrag] = useState(false);

    const onDragEnter = (event: Event) => {
        event.preventDefault();
        setDrag(true);
    };
    const onDragLeave = (event: Event) => {
        event.preventDefault();
        setDrag(false);
    };

    useEffect(() => {
        window.addEventListener('dragover', onDragEnter);
        window.addEventListener('drop', onDragLeave);
        window.addEventListener('dragleave', onDragLeave);
        return () => {
            window.removeEventListener('dragover', onDragEnter);
            window.removeEventListener('drop', onDragLeave);
            window.removeEventListener('dragleave', onDragLeave);
        };
    });

    return (
        <QuestionLayout
            {...props}
            type={QuestionLayoutType.Column}
            className={cnFileQuestion('', [className])}
            positions={fileQuestionPositions}
            label={label}
            control={
                <div className={cnFileQuestion('Input')}>
                    {files.length < maxCount &&
                        (drag ? (
                            <FileDropZone
                                view="compact"
                                onAdd={onSelectFiles}
                                title={drugText ?? buttonText}
                                description={hint}
                                multiple={maxCount > 1}
                                aria-label={buttonText}
                                className={cnFileQuestion('DropZone')}
                                {...getTextInputAria(
                                    props.required,
                                    Boolean(props.error),
                                    props.id,
                                )}
                            />
                        ) : (
                            <div className={cnFileQuestion('Load')}>
                                <input
                                    {...controlProps}
                                    multiple={maxCount > 1}
                                    aria-label={buttonText}
                                    {...getTextInputAria(
                                        props.required,
                                        Boolean(props.error),
                                        props.id,
                                    )}
                                    name={props.name}
                                />
                                <div>
                                    <Button
                                        {...buttonProps}
                                        {...triggerProps}
                                        ref={inputRef}
                                        className={cnFileQuestion('Button')}
                                        extraProps={{
                                            'aria-describedby': `${props.id}-hint`,
                                        }}
                                    >
                                        <Button.Icon>{buttonIcon}</Button.Icon>
                                        {buttonText}
                                    </Button>
                                </div>
                                <span className={cnFileQuestion('Hint')} id={`${props.id}-hint`}>
                                    {hint}
                                </span>
                            </div>
                        ))}
                    {files.length > 0 && (
                        <div className={cnFileQuestion('Files')}>
                            {files.map((file, index) => (
                                <FileBadge
                                    key={file.path ?? file.name}
                                    name={file.name}
                                    loading={file.loading}
                                    onDelete={onDelete(file, index)}
                                    url={file.url}
                                />
                            ))}
                        </div>
                    )}
                </div>
            }
        />
    );
});
export default FileQuestion;
